import { calculatePriceTierDiscount } from "../calculatePriceTierDiscount/index.js";
import { filterApplicableVolumeDiscounts } from "../filterApplicableVolumeDiscounts/index.js";
import type {
  PriceTierDiscountItemType,
  PriceTierDiscountType,
} from "../priceTierDiscountCommons.js";

type OrderItem = {
  quantity: number;
  volume: number | null;
  unit: string | null;
  packQuantity: number | null;
  containerType: string | null;
  price: number;
  discountable: boolean;
};

type CaseSizeRule = {
  quantity: number;
  containerType: "can" | "bottle";
  volume: number;
  unit: string;
};

export type VolumeDiscount = {
  id: number;
  deletedAt: Date | null;
  type: PriceTierDiscountType;
  amount: number;
  quantity: number;
  itemType: PriceTierDiscountItemType | null;
  minimumBasketValue: number;
  VolumeDiscountsId: number | null;
};

type VolumeDiscountKey = string;

const createKey = (item: PriceTierDiscountItemType | "any") => {
  return `${item}`;
};

export const findBiggestApplicableVolumeDiscounts = ({
  volumeDiscountList,
  orderItemList,
  caseSizeRuleList,
}: {
  volumeDiscountList: VolumeDiscount[];
  orderItemList: OrderItem[];
  caseSizeRuleList: CaseSizeRule[];
}) => {
  const applicableVolumeDiscounts = filterApplicableVolumeDiscounts({
    orderItemList,
    caseSizeRuleList,
    volumeDiscountList,
  });

  const biggestApplicableVolumeDiscountsMap = new Map<
    VolumeDiscountKey,
    { volumeDiscount: VolumeDiscount; discountAmount: number }
  >();

  for (const volumeDiscount of applicableVolumeDiscounts) {
    const volumeDiscountKey = createKey(volumeDiscount?.itemType ?? "any"); // 'null' itemType is actually 'any'

    if (!volumeDiscountKey) {
      continue;
    }

    // calculate volume discount amount

    const newVolumeDiscountAmount = calculatePriceTierDiscount({
      orderItems: orderItemList,
      priceTierDiscount: volumeDiscount,
      caseSizeRuleList,
    });

    // if existing volume discount found

    const volumeDiscountFound =
      biggestApplicableVolumeDiscountsMap.get(volumeDiscountKey);

    if (volumeDiscountFound) {
      const currentVolumeDiscountAmount = volumeDiscountFound.discountAmount;

      // new volume discount of same container type is larger, replace

      if (newVolumeDiscountAmount > currentVolumeDiscountAmount) {
        biggestApplicableVolumeDiscountsMap.set(volumeDiscountKey, {
          volumeDiscount,
          discountAmount: newVolumeDiscountAmount,
        });
      }

      continue;
    }

    // add volume discount with discount amount to map

    biggestApplicableVolumeDiscountsMap.set(volumeDiscountKey, {
      volumeDiscount,
      discountAmount: newVolumeDiscountAmount,
    });
  }

  return Array.from(
    biggestApplicableVolumeDiscountsMap,
    ([, { volumeDiscount, discountAmount }]) => {
      return {
        volumeDiscount,
        discountAmount,
      };
    },
  );
};
